// Language Context for managing application language
import React, { createContext, useState, useContext, useEffect } from 'react';
import { translations } from '../i18n/translations';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// What to show someone who has never been here before.
//
// A stored choice always wins - guessing again after the visitor has told us
// would be worse than not guessing at all. Only on a first visit do we read the
// browser's preference, and the selector sits in the header either way, so a
// wrong guess costs one click.
export const detectLanguage = () => {
  const stored = localStorage.getItem('language');
  if (stored && translations[stored]) return stored;

  const preferred = navigator.languages || [navigator.language || ''];
  for (const tag of preferred) {
    // "sv-SE" and "sv" both mean Swedish to us.
    const base = String(tag).toLowerCase().split('-')[0];
    if (translations[base]) return base;
  }

  // Finnish rather than English: this is a Finnish series' site, and most
  // visitors who are not asking for anything specific want Finnish.
  return 'fi';
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(detectLanguage);

  const [exportLanguage, setExportLanguage] = useState(() => {
    // Get export language from localStorage or default to current language
    const stored = localStorage.getItem('exportLanguage');
    return stored && translations[stored] ? stored : language;
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    // Screen readers pick their voice from this, and browsers decide whether to
    // offer a translation by it. Hardcoded to "en" in index.html and never
    // updated until now, which told every assistive technology that a page of
    // Finnish was English.
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    localStorage.setItem('exportLanguage', exportLanguage);
  }, [exportLanguage]);

  const t = (key) => {
    return translations[language]?.[key] || key;
  };

  const changeLanguage = (newLanguage) => {
    if (translations[newLanguage]) {
      setLanguage(newLanguage);
    }
  };

  const changeExportLanguage = (newLanguage) => {
    if (translations[newLanguage]) {
      setExportLanguage(newLanguage);
    }
  };

  const availableLanguages = [
    { code: 'fi', name: 'Suomi' },
    { code: 'en', name: 'English' },
    { code: 'sv', name: 'Svenska' }
  ];

  const value = {
    language,
    exportLanguage,
    t,
    changeLanguage,
    changeExportLanguage,
    availableLanguages
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
